import axiosInstance from '../axiosInstance';

/**
 * Payroll — Staff > Payroll.
 *
 * Two jobs: set what people are paid, and report what they are owed for a
 * period. Manager-only; the API enforces that independently of anything the UI
 * decides to show.
 *
 * No money is calculated on the client. Pay comes back already worked out —
 * hours x the person's hourly rate, done once on the server so this and every
 * other surface that reports pay agree. See api/utils/payrollService.js.
 *
 * The rate lives in ONE place (`user_employment_details.hourly_rate`) and is read
 * live, so changing it also changes what an earlier month exports as.
 */
const payrollService = {
  /**
   * The pay run for a period.
   *
   * Pass `{ month: 'YYYY-MM' }` for a whole month, or `{ from, to }` for an
   * explicit range; from/to wins. With neither, the API returns the current
   * month rather than an error.
   *
   * Returns `{ from, to, rows, employees, exceptions, summary }`.
   *   employees  — per-person totals, INCLUDING anyone with no hours, so a
   *                missing name never reads as an oversight.
   *   exceptions — work that was NOT paid, with the reason and the manager's
   *                next action. Never silently dropped.
   *   summary    — plus `needs_rate`: people who worked but have no rate set,
   *                the one thing that makes a total worth questioning.
   */
  getPayroll: async ({ from, to, month, employeeId } = {}) => {
    const res = await axiosInstance.get('/carehome-manager/payroll', {
      params: {
        from: from || undefined,
        to: to || undefined,
        month: month || undefined,
        employee_id: employeeId || undefined,
      },
    });
    return res.data;
  },

  /**
   * Every staff member and their current hourly rate.
   *
   * A row's `suggested_rate` is a best-effort read of the OLD free-text pay rate
   * field, present only where no numeric rate is set. It is a suggestion for the
   * manager to confirm — never applied on its own, because that field is free
   * text and "£14.50/hr + £2 nights" must not quietly become £14.50.
   */
  getRates: async () => {
    const res = await axiosInstance.get('/carehome-manager/payroll/rates');
    return res.data;
  },

  /**
   * Set (or clear, with null) one person's hourly rate.
   *
   * Applies everywhere, including earlier months — the rate is read live, not
   * recorded per shift. The API's success message says so, which is worth
   * surfacing verbatim.
   */
  updateRate: async (employeeId, hourlyRate) => {
    const res = await axiosInstance.patch(
      `/carehome-manager/payroll/rates/${employeeId}`,
      { hourly_rate: hourlyRate },
    );
    return res.data;
  },

  /**
   * Apply a whole spreadsheet of hourly rates at once.
   *
   * The CSV needs a header row: a staff column (`employee_code`, the login id
   * shown on screen, or `employee_id`) and a rate column (`hourly_rate`).
   * Columns are matched by name, so their order does not matter.
   *
   * `preview: true` reports what WOULD change without writing anything. Either
   * way the API applies nothing unless every row is valid — a half-applied rates
   * file would leave some people quietly on the old figure at the next pay run.
   *
   * @param {File} file
   * @param {{preview?: boolean}} [opts]
   */
  importRates: async (file, { preview = false } = {}) => {
    const body = new FormData();
    body.append('file', file);

    const res = await axiosInstance.post(
      `/carehome-manager/payroll/rates/import${preview ? '?preview=true' : ''}`,
      body,
      // The instance defaults to application/json; undefined makes axios drop
      // the header so the browser sets multipart with its own boundary.
      { headers: { 'Content-Type': undefined } },
    );
    return res.data;
  },
};

export default payrollService;
